import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { Pencil02 } from 'untitledui-js-base';
import { useCommonApi } from '../../api/useCommonApi';
import { useNotify } from '../../hooks';
import { b64ToBlob } from '../../utils/b64ToBlob';
import { SignatureDialog } from '../common/SignatureDialog';
import { getErrorCode } from '../sourceCommissions/utils';

const DATE_FORMAT = 'dd.MM.yyyy HH:mm';

const ORDER_FORM_ERRORS: Record<number, string> = {
  402: 'Nehoda nepatří přihlášenému obchodníkovi.',
  404: 'Nehoda neexistuje.',
  431: 'K nehodě není přiřazený servis. Nejdřív přiřaďte servis.',
  447: 'Zakázkový list ještě nebyl vygenerován.',
  448: 'Chybí podpis.',
  421: 'Dokument se nepodařilo vygenerovat. Zkontrolujte data nájemce a vozu.'
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

interface ServiceOrderFormDialogProps {
  open: boolean;
  onClose: () => void;
  accidentId: number;
  hasOrderForm: boolean;
}

export const ServiceOrderFormDialog = ({ open, onClose, accidentId, hasOrderForm }: ServiceOrderFormDialogProps) => {
  const { notify } = useNotify();
  const [handoverDate, setHandoverDate] = useState<Date | null>(new Date());
  const [takeoverDate, setTakeoverDate] = useState<Date | null>(new Date());
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [signatureOpen, setSignatureOpen] = useState(false);

  const { mutate: preview, isPending: isPreviewing } = useMutation(useCommonApi().previewServiceOrderForm());
  const { mutate: generate, isPending: isGenerating } = useMutation(useCommonApi().generateServiceOrderForm());
  const { mutate: download, isPending: isDownloading } = useMutation(useCommonApi().downloadServiceOrderForm());

  useEffect(() => {
    if (open) {
      setHandoverDate(new Date());
      setTakeoverDate(new Date());
    } else {
      setPreviewUrl(null);
    }
  }, [open]);

  useEffect(() => () => (previewUrl ? URL.revokeObjectURL(previewUrl) : undefined), [previewUrl]);

  const onError = (error: unknown) => {
    const code = getErrorCode(error);
    notify('error', (code && ORDER_FORM_ERRORS[code]) || 'Došlo k chybě. Zkuste to prosím znovu.');
  };

  const handlePreview = () =>
    preview(
      { accidentId },
      {
        onSuccess: (res) => setPreviewUrl(URL.createObjectURL(b64ToBlob(res.file))),
        onError
      }
    );

  const handleSign = (signature: string) =>
    generate(
      {
        accidentId,
        signature,
        handoverDate: format(handoverDate ?? new Date(), DATE_FORMAT),
        takeoverDate: format(takeoverDate ?? new Date(), DATE_FORMAT)
      },
      {
        onSuccess: () => {
          setSignatureOpen(false);
          notify('success', 'Zakázkový list byl podepsán.');
        },
        onError
      }
    );

  const handleDownload = () =>
    download(
      { accidentId },
      {
        onSuccess: (res) => downloadBlob(b64ToBlob(res.file), res.filename),
        onError
      }
    );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Zakázkový list</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DateTimePicker
                label="Předání"
                value={handoverDate}
                onChange={setHandoverDate}
                format={DATE_FORMAT}
                sx={{ flex: 1 }}
              />
              <DateTimePicker
                label="Převzetí"
                value={takeoverDate}
                onChange={setTakeoverDate}
                format={DATE_FORMAT}
                sx={{ flex: 1 }}
              />
            </Stack>
            {previewUrl && (
              <Box component="iframe" src={previewUrl} title="Náhled" sx={{ width: 1, height: 500, border: 0 }} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={onClose}>Zavřít</Button>
          {hasOrderForm && (
            <Button variant="outlined" onClick={handleDownload} loading={isDownloading}>
              Stáhnout
            </Button>
          )}
          <Button variant="outlined" onClick={handlePreview} loading={isPreviewing}>
            Náhled
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Pencil02 size="20px" />}
            onClick={() => setSignatureOpen(true)}
          >
            Podepsat
          </Button>
        </DialogActions>
      </Dialog>
      <SignatureDialog
        open={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSave={handleSign}
        loading={isGenerating}
      />
    </>
  );
};
